import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    IconButton,
    Snackbar,
    Alert,
    Typography,
    CircularProgress,
    Grid,
    Skeleton,
    Container, 
    useTheme, 
    useMediaQuery,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import _ from 'lodash';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { MenuItem } from '@mui/material';
import { BetweenHorizontalStart, Camera, ScanEye, CircleHelp } from 'lucide-react';
import SmartDailyDescriptionInput from './SmartDailyDescriptionInput';
import { useRefresh } from '../contexts/RefreshContext';


const commonPaperShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
const commonBorderRadius = 2; 
const commonBackgroundColor = 'rgba(255, 255, 255, 0.9)';

const hideScrollbarStyle = {
    '&::-webkit-scrollbar': {
        display: 'none',
    },
    '-ms-overflow-style': 'none',  
    'scrollbar-width': 'none',  
};

const convertToYYYYMMDD = (dateStringMMDDYYYY) => {
    if (!dateStringMMDDYYYY || typeof dateStringMMDDYYYY !== 'string') {
        return null; 
    }
    const parts = dateStringMMDDYYYY.split('/');
    if (parts.length === 3) {
        return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
    }
    return null; 
};

const EmployeeForm = () => {
    const { user, loading: authLoading } = useAuth();
    const loggedInEmployeeId = user?.id || null;
    const { refreshKey } = useRefresh();
    const [file, setFile] = useState(null);
    const [sales, setSales] = useState([]);
    const [dashboardData, setDashboardData] = useState(null);
    const [hotelsList, setHotelsList] = useState([]);
    const [serviceOptions, setServiceOptions] = useState([]);
    const [helpOpen, setHelpOpen] = useState(false);
    const [headerLoading, setHeaderLoading] = useState(true);
    const [reparsingIndex, setReparsingIndex] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [submissionResult, setSubmissionResult] = useState(null);
    const fileInputRef = useRef();
    const theme = useTheme(); 
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); 
    const [loadingUpload, setLoadingUpload] = useState(false); 
    const [loadingSubmit, setLoadingSubmit] = useState(false); 
     
    useEffect(() => {
        const loadInitialData = async () => {
            setHeaderLoading(true);
            try {
                const [dashboardResponse, hotelsResponse] = await Promise.all([
                    api.get('/dashboard/me'),
                    api.get('/hotels')
                ]);
                setDashboardData({
                    name: dashboardResponse.data.name || 'N/A',
                    resort: dashboardResponse.data.resort || 'N/A',
                    date: dashboardResponse.data.date_string || 'N/A',
                });
                setHotelsList(hotelsResponse.data || []);

                // Abreviações para o autocomplete. Falha aqui não pode derrubar a
                // tela — sem a lista o campo só volta a ser texto puro.
                api.get('/services/abbreviations')
                    .then(res => setServiceOptions(res.data || []))
                    .catch(() => setServiceOptions([]));
            } catch (error) {
                console.error("Error fetching/refreshing data:", error);
                const errorMsg = error.response?.data?.msg || 'Error loading/refreshing data.';
                setSnackbar({ open: true, message: errorMsg, severity: 'error' });
                setDashboardData({ name: 'Error', resort: 'Error', date: 'Error' });
                setHotelsList([]);
            } finally {
                setHeaderLoading(false);
            }
        };

        if (loggedInEmployeeId && !authLoading) {
            loadInitialData();
        } else if (!authLoading && !loggedInEmployeeId) {
             setSnackbar({ open: true, message: 'User ID not found. Please log in again.', severity: 'error' });
             setHeaderLoading(false);
             setDashboardData({ name: 'Login Error', resort: 'Login Error', date: 'Login Error' });
             setHotelsList([]);
        }
    }, [loggedInEmployeeId, authLoading, refreshKey]);


    const handleUpload = async () => {
        if (!file || !dashboardData || dashboardData.date === 'Error' || dashboardData.date === 'Login Error' || dashboardData.date === 'N/A') {
            setSnackbar({ open: true, message: 'Employee data (especially date) not loaded or file not selected.', severity: 'warning' });
            return;
        }
        setSubmissionResult(null);
        setLoadingUpload(true);
        try {
            const form = new FormData();
            form.append('file', file);
            const response = await api.post('/api/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
            const data = response.data.form_data;
            setSales(data.sales ? data.sales.map((sale, index) => ({
                form_number: sale.form_number || (index + 1),
                original_description: sale.original_description || '',
                parsed_description: sale.parsed_description || '',
                payment_method: sale.payment_method || '',
                tip: sale.tip === null || sale.tip === undefined ? '' : sale.tip,
                calculated_total: sale.calculated_total === null || sale.calculated_total === undefined ? 0 : sale.calculated_total,
                items: sale.items || [],
                invalid: (!sale.items || sale.items.length === 0 ||
                    (sale.parsed_description && (sale.parsed_description.toLowerCase().includes('error') || sale.parsed_description.toLowerCase().includes('falha'))) ||
                    (sale.items && sale.items.some(item => !item.service_id || item.quantity == null || isNaN(item.quantity) || Number(item.quantity) <= 0 || item.unit_price == null || isNaN(item.unit_price)))
                )
            })) : []);
            if (fileInputRef.current) fileInputRef.current.value = "";
            setFile(null);
        } catch (err) {
            console.error('Error processing image:', err);
            const errorMsg = err.response?.data?.msg || 'Error processing image';
            setSnackbar({ open: true, message: errorMsg, severity: 'error' });
        } finally {
            setLoadingUpload(false);
        }
    };

     const debouncedReparse = useCallback(
        _.debounce(async (index, description) => {
            setReparsingIndex(index);
            try {
                const response = await api.post('/api/reparse-sale', { description });
                const { parsed_description, calculated_total, items } = response.data;
                setSales(currentSales =>
                    currentSales.map((sale, i) => {
                        if (i === index) {
                            const hasError = !items || items.length === 0 ||
                                (parsed_description && (parsed_description.toLowerCase().includes('error') || parsed_description.toLowerCase().includes('falha'))) ||
                                (items && items.some(item => !item.service_id || item.quantity == null || isNaN(item.quantity) || Number(item.quantity) <= 0 || item.unit_price == null || isNaN(item.unit_price)));
                            return {
                                ...sale,
                                original_description: description,
                                parsed_description: parsed_description,
                                calculated_total: calculated_total,
                                items: items || [],
                                invalid: hasError
                            };
                        }
                        return sale;
                    })
                );
            } catch (error) {
                console.error('Error reparsing sale:', error);
                const errorMsg = error.response?.data?.msg || 'Error reprocessing line';
                setSnackbar({ open: true, message: `${errorMsg} (Line ${index + 1})`, severity: 'warning' });
                setSales(currentSales =>
                    currentSales.map((sale, i) => i === index ? { ...sale, invalid: true, parsed_description: `(Reprocessing Failed: ${errorMsg})` } : sale)
                );
            } finally {
                setReparsingIndex(null);
            }
        }, 1300),
        []
    );

    const handleAddSale = () => {
        setSales(prevSales => [
            ...prevSales,
            {
                form_number: prevSales.length > 0 ? Math.max(...prevSales.map(s => s.form_number || 0)) + 1 : 1,
                original_description: '',
                parsed_description: '',
                payment_method: '',
                tip: '',
                calculated_total: 0,
                items: [],
                invalid: true 
            }
        ]);
    };

      const handleFieldChange = useCallback(
        (index, field, value) => {
            let needsReparse = false;
            const updatedSales = sales.map((sale, i) => {
                if (i === index) {
                     const updatedSale = { ...sale };
                    if (field === 'tip') {
                        const numericValue = value === '' ? '' : parseFloat(value);
                        updatedSale[field] = isNaN(numericValue) ? '' : numericValue;
                    } else {
                        updatedSale[field] = value;
                    }
                    if (field === 'original_description') {
                        needsReparse = true;
                    }
                    updatedSale.invalid = (
                        !updatedSale.items || updatedSale.items.length === 0 ||
                        (updatedSale.parsed_description && (updatedSale.parsed_description.toLowerCase().includes('error') || updatedSale.parsed_description.toLowerCase().includes('falha'))) ||
                        (updatedSale.items && updatedSale.items.some(item => !item.service_id || item.quantity == null || isNaN(item.quantity) || Number(item.quantity) <= 0 || item.unit_price == null || isNaN(item.unit_price))) ||
                        !updatedSale.payment_method
                    );
                    return updatedSale;
                }
                return sale;
            });
            setSales(updatedSales);
            if (needsReparse && value.trim()) {
                debouncedReparse(index, value);
            } else if (needsReparse && !value.trim()){
                setSales(currentSales =>
                    currentSales.map((sale, i) => 
                        i === index ? { ...sale, parsed_description: '', calculated_total: 0, items: [], invalid: true } : sale
                    )
                );
            }
        }, [sales, debouncedReparse]
    );


    const handleRemoveSale = useCallback(
        (index) => {
            setSales(currentSales => {
                const updatedSales = currentSales.filter((_, i) => i !== index);
                 return updatedSales.map((sale, newIndex) => ({ ...sale, form_number: sale.form_number || (newIndex + 1) }));
            });
        }, []
    );

    const handleSubmit = async () => {
        if (!dashboardData || headerLoading || dashboardData.date === 'Error' || dashboardData.date === 'Login Error' || dashboardData.date === 'N/A') {
            setSnackbar({ open: true, message: 'Wait for header data to load (especially date).', severity: 'warning' });
            return;
        }
        if (!dashboardData?.resort || dashboardData.resort === 'N/A' || dashboardData.resort === 'OFF') {
            setSnackbar({ open: true, message: 'Invalid resort.', severity: 'error' });
            return;
        }
        if (!loggedInEmployeeId) {
            setSnackbar({ open: true, message: 'Logged-in employee ID not found.', severity: 'error' });
            return;
        }
        if (reparsingIndex !== null) {
            setSnackbar({ open: true, message: 'Wait for the line being edited to finish processing.', severity: 'warning' });
            return;
        }
        if (sales.length === 0) {
            setSnackbar({ open: true, message: 'No sales to submit.', severity: 'warning' });
            return;
        }
    
        const invalidSale = sales.find(sale => sale.invalid || !sale.payment_method);
        if (invalidSale) {
            const saleNumber = invalidSale.form_number || (sales.indexOf(invalidSale) + 1);
            let message = `Sale #${saleNumber} contains errors. `;
            if (!invalidSale.payment_method) message += 'Payment method is required. ';
            if (invalidSale.invalid && invalidSale.payment_method) message += 'Check items or description. ';
            setSnackbar({ open: true, message: message.trim() + 'Please fix before submitting.', severity: 'error' });
            return;
        }

        const saleDateForApi = convertToYYYYMMDD(dashboardData.date);
        if (!saleDateForApi) {
            setSnackbar({ open: true, message: 'Invalid date format in header. Cannot submit.', severity: 'error' });
            return;
        }
    
        setSubmissionResult(null);
        setLoadingSubmit(true);
        try {
            const matchedHotel = hotelsList.find(hotel => hotel.name === dashboardData.resort);
            if (!matchedHotel) {
                setSnackbar({ open: true, message: `Hotel/Resort "${dashboardData.resort}" not found in list. Cannot submit.`, severity: 'error'});
                setLoadingSubmit(false);
                return;
            }
            const payload = {
                header: {
                    employee_id: loggedInEmployeeId,
                    hotel_id: matchedHotel.id,
                    sale_date: saleDateForApi
                },
                sales: sales.map(sale => ({
                    form_number: sale.form_number,
                    payment_method: sale.payment_method || 'Unknown',
                    tip: sale.tip === '' || sale.tip === null || isNaN(parseFloat(sale.tip)) ? 0 : Number(sale.tip),
                    items: sale.items.map(item => ({
                        service_id: item.service_id,
                        quantity: Number(item.quantity),
                        unit_price: Number(item.unit_price)
                    }))
                }))
            };
            const response = await api.post('/sales/form-submit', payload);
            if (response.data && response.data.sales_mapping) {
                setSubmissionResult(response.data.sales_mapping);
            }
            setSnackbar({ open: true, message: response.data.msg || 'Sales saved successfully!', severity: 'success' });
            setSales([]);
            setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
        } catch (err) {
            console.error('Error submitting sales form:', err);
            const errorMsg = err.response?.data?.msg || 'Error submitting sales';
            const errorDetail = err.response?.data?.detail || err.response?.data?.error || '';
            setSnackbar({ open: true, message: `${errorMsg} ${errorDetail ? `: ${errorDetail}` : ''}`.trim(), severity: 'error' });
            setSubmissionResult(null);
        } finally {
            setLoadingSubmit(false);
        }
    };

    return (
        <Container maxWidth="md" sx={{ py: { xs: 1, sm: 1 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>

                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 1, sm: 2 },
                        borderRadius: commonBorderRadius,
                        backgroundColor: commonBackgroundColor,
                        boxShadow: commonPaperShadow,
                        width: '100%',
                        textAlign: 'center',
                    }}
                >
                    <Grid container spacing={1} alignItems="center" justifyContent="center">

                        <Grid item xs="auto">
                            <Tooltip title="How to use Smart Daily">
                                <IconButton
                                    onClick={() => setHelpOpen(true)}
                                    color="primary"
                                    aria-label="How to use Smart Daily"
                                >
                                    <CircleHelp />
                                </IconButton>
                            </Tooltip>
                        </Grid>

                        <Grid item xs="auto">
                            <Button
                                startIcon={<BetweenHorizontalStart />}
                                variant="outlined"
                                onClick={handleAddSale}
                                fullWidth
                            >
                                Add Sale
                            </Button>
                        </Grid>

                        <Grid item xs="auto">
                            <Button
                                startIcon={<Camera/>}
                                variant="outlined"
                                onClick={() => fileInputRef.current.click()}
                                fullWidth
                            >
                                Select Image
                            </Button>
                            <input
                                type="file"
                                accept="image/*,application/pdf"
                                ref={fileInputRef}
                                onChange={(e) => setFile(e.target.files[0])}
                                style={{ display: 'none' }}
                            />
                        </Grid>

                        <Grid item xs="auto">
                            <Button
                                variant="contained"
                                onClick={handleUpload}
                                disabled={loadingUpload || headerLoading || !file || !dashboardData || !loggedInEmployeeId || dashboardData.resort === 'Error' || dashboardData.resort === 'Login Error' || dashboardData.date === 'N/A' || dashboardData.date === 'Error'}
                                startIcon={loadingUpload && reparsingIndex === null ? <CircularProgress size={20} color="inherit" /> : <ScanEye />}
                                fullWidth
                            >
                                {loadingUpload && reparsingIndex === null ? 'Processing...' : 'Process File'}
                            </Button>
                        </Grid>

                    </Grid>

                    {file && (
                        <Typography variant="body2" noWrap title={file.name} sx={{ mt: 1 }}>
                            File: {file.name}
                        </Typography>
                    )}
                </Paper>

                <Paper
                    elevation={0} 
                    sx={{
                        p: {xs: 1, sm: 2}, 
                        borderRadius: commonBorderRadius,
                        backgroundColor: commonBackgroundColor,
                        boxShadow: commonPaperShadow,
                        width: '100%', 
                        textAlign: 'center', 
                    }}
                >
                    <Grid container spacing={isMobile ? 1 : 2} justifyContent="center">
                        <Grid item xs={12} sm={4}>
                            {headerLoading ? <Skeleton variant="text" height={40} /> : (
                                <TextField label="Name" value={dashboardData?.name || ''} fullWidth InputProps={{ readOnly: true }} variant="filled" size="small" sx={{ textAlign: 'center' }} />
                            )}
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            {headerLoading ? <Skeleton variant="text" height={40} /> : (
                                <TextField label="Resort (Today)" value={dashboardData?.resort || ''} fullWidth InputProps={{ readOnly: true }} variant="filled" size="small" sx={{ textAlign: 'center' }}/>
                            )}
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            {headerLoading ? <Skeleton variant="text" height={40} /> : (
                                <TextField label="Date (Today)" value={dashboardData?.date || ''} fullWidth InputProps={{ readOnly: true }} variant="filled" size="small" sx={{ textAlign: 'center' }}/>
                            )}
                        </Grid>
                    </Grid>
                </Paper>

                 {sales.length > 0 && (
                     <>
                        <TableContainer
                            component={Paper} 
                            elevation={0} 
                            sx={{
                                p: { xs: 1, sm: 2 }, 
                                borderRadius: commonBorderRadius,
                                backgroundColor: commonBackgroundColor, 
                                boxShadow: commonPaperShadow, 
                                width: '100%', 
                                overflowX: 'auto', 
                                ...hideScrollbarStyle 
                            }}
                        >
                            <Table stickyHeader size="small" sx={{ minWidth: 650 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold', width: '5%' }}>#</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Description (Editable)</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Payment</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Tip ($)</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', width: '15%', textAlign: 'right' }}>Total ($)</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', width: '10%', textAlign: 'center' }}>Delete</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sales.map((sale, index) => (
                                        <TableRow
                                            key={sale.form_number || `temp-${index}`}
                        
                                        >
                                            <TableCell component="th" scope="row">{sale.form_number}</TableCell>
                                            <TableCell>
                                                <SmartDailyDescriptionInput
                                                    value={sale.original_description}
                                                    onChange={(next) => handleFieldChange(index, 'original_description', next)}
                                                    services={serviceOptions}
                                                    size="small"
                                                    fullWidth
                                                    multiline
                                                    minRows={isMobile ? 2 : 1}
                                                    maxRows={4}
                                                    placeholder="Ex: 1 CB + 2 CH"
                                                    error={sale.invalid}
                                                    InputProps={{
                                                        endAdornment: reparsingIndex === index ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null,
                                                    }}
                                                />
                                                {(sale.parsed_description || sale.invalid) && (
                                                <Box sx={{ mt: 0.5 }}>
                                                    {(sale.invalid || (sale.parsed_description && sale.parsed_description.includes('Unknown item:'))) && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                        color: 'error.dark',
                                                        fontWeight: 'bold',
                                                        display: 'block',
                                                        whiteSpace: 'pre-wrap'
                                                        }}
                                                    >
                                                        {
                                                        (() => {
                                                            if (sale.parsed_description?.includes('Unknown item:')) {
                                                            return 'Unknown Item';
                                                            }
                                                            if (sale.invalid) {
                                                            return 'Invalid quantity';
                                                            }
                                                            return '';
                                                        })()
                                                        }
                                                    </Typography>
                                                    )}

                                                    {sale.parsed_description && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                        color: 'text.secondary',
                                                        display: 'block',
                                                        whiteSpace: 'pre-wrap'
                                                        }}
                                                    >
                                                        {
                                                        (() => {
                                                            let text = sale.parsed_description;
                                                            text = text.replace(/\(.*?\)/g, '').trim(); 
                                                            return text;
                                                        })()
                                                        }
                                                    </Typography>
                                                    )}
                                                </Box>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    select
                                                    value={sale.payment_method || ''}
                                                    onChange={(e) => handleFieldChange(index, 'payment_method', e.target.value)}
                                                    size="small"
                                                    fullWidth
                                                    error={!sale.payment_method}  
                                                    placeholder="RC/CA/CC/SQ"
                                                    >
                                                    <MenuItem value="">Select</MenuItem>
                                                    <MenuItem value="RC">Room Charge</MenuItem>
                                                    <MenuItem value="CA">Cash</MenuItem>
                                                    <MenuItem value="CC">Credit Card</MenuItem>
                                                    <MenuItem value="SQ">Square</MenuItem>
                                                </TextField>
                                            </TableCell>
                                            <TableCell>
                                                <TextField value={sale.tip} onChange={(e) => handleFieldChange(index, 'tip', e.target.value)} size="small" fullWidth type="number" inputProps={{ step: "0.01", min: "0" }} placeholder="0.00" sx={{ minWidth: 70 }} />
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right', fontWeight: 500, verticalAlign: 'middle' }}>
                                                {typeof sale.calculated_total === 'number' ? `$${sale.calculated_total.toFixed(2)}` : 'N/A'}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                <IconButton onClick={() => handleRemoveSale(index)} size="small" color="error" aria-label="Delete sale">
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Paper
                            elevation={0} 
                            sx={{
                                p: {xs: 1, sm: 2}, 
                                borderRadius: commonBorderRadius,
                                background: commonBackgroundColor, 
                                boxShadow: commonPaperShadow,
                                width: '100%'
                            }}
                        >
                            <Grid container spacing={1} justifyContent="center">
                                {['CA', 'RC', 'CC', 'SQ'].map((method) => {
                                    const total = sales
                                        .filter(sale => !sale.invalid && (sale.payment_method || '').toUpperCase() === method)
                                        .reduce((sum, sale) => sum + (sale.calculated_total || 0), 0);
                                    return (
                                        <Grid item xs={6} sm={3} key={method}>
                                            <Paper elevation={0} sx={{ p: {xs: 1, sm: 1.5}, textAlign: 'center', borderRadius: 1, backgroundColor: '#fafafa' }}>
                                                <Typography variant="subtitle2" color="text.secondary">{method}</Typography>
                                                <Typography variant={isMobile? "body1" : "h6"} sx={{ fontWeight: 'bold' }}>${total.toFixed(2)}</Typography>
                                            </Paper>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                            <Grid container spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                                <Grid item xs={6} sm={4}>
                                    <Paper elevation={0} sx={{ p: {xs: 1, sm: 1.5}, textAlign: 'center', borderRadius: 1, backgroundColor: '#fafafa' }}>
                                        <Typography variant="subtitle2" color="text.secondary">Total Sales</Typography>
                                        <Typography variant={isMobile ? "body1" : "h6"} sx={{ fontWeight: 'bold' }}>
                                            {sales.length}
                                        </Typography>
                                    </Paper>
                                </Grid>

                                <Grid item xs={6} sm={4}>
                                    <Paper elevation={0} sx={{ p: {xs: 1, sm: 1.5}, textAlign: 'center', borderRadius: 1, backgroundColor: '#fafafa' }}>
                                        <Typography variant="subtitle2" color="text.secondary">Total Tips</Typography>
                                        <Typography variant={isMobile ? "body1" : "h6"} sx={{ fontWeight: 'bold' }}>
                                            ${sales.reduce((sum, sale) => sum + (sale.invalid ? 0 : parseFloat(sale.tip) || 0), 0).toFixed(2)}
                                        </Typography>
                                    </Paper>
                                </Grid>

                                  <Grid item xs={12} sm={8}>
                                       <Paper elevation={0} sx={{ p: {xs: 1, sm: 1.5}, textAlign: 'center', borderRadius: 1, backgroundColor: '#e0f7fa' }}>
                                           <Typography variant="subtitle2" color="text.secondary">Grand Total</Typography>
                                           <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 'bold', color: '#00796b' }}>
                                               ${(sales.reduce((sum, sale) => sum + (sale.invalid ? 0 : sale.calculated_total || 0), 0)).toFixed(2)}
                                           </Typography>
                                       </Paper>
                                   </Grid>
                               </Grid>
                        </Paper>

                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                justifyContent: 'center',
                                mt: 0
                            }}
                        >
                          <Button
                              variant="contained"
                              color="primary"
                              onClick={handleSubmit}
                              disabled={
                                  loadingSubmit ||
                                  headerLoading ||
                                  reparsingIndex !== null ||
                                  !dashboardData ||
                                  !loggedInEmployeeId ||
                                  sales.length === 0 ||
                                  sales.some(sale => sale.invalid) ||
                                  sales.some(sale => !sale.payment_method) ||
                                  !dashboardData?.resort ||
                                  dashboardData.resort === 'N/A' ||
                                  dashboardData.resort === 'OFF'
                              }
                              startIcon={loadingSubmit && reparsingIndex === null ? <CircularProgress size={20} color="inherit" /> : null}
                              sx={{ minWidth: '200px', mt: 0 }}
                          >
                              {loadingSubmit && reparsingIndex === null ? 'Submitting...' : 'Confirm and Submit Sales'}
                          </Button>
                        </Box>
                    </>
                )} 


                {submissionResult && submissionResult.length > 0 && (
                <Container maxWidth="md" sx={{ py: { xs: 1, sm: 1 } }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    {submissionResult && submissionResult.length > 0 && (
                    <Paper
                        elevation={0}
                        sx={{
                        p: { xs: 3, sm: 3 },
                        borderRadius: commonBorderRadius,
                        backgroundColor: '#ffffff',
                        boxShadow: commonPaperShadow,
                        width: '100%',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        }}
                    >
                        <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ fontWeight: 'bold', color: 'text.primary' }}
                        >
                        Submission Summary
                        </Typography>
                
                        <Box
                        sx={{
                            width: '100%',
                            maxHeight: 300,
                            overflowY: 'auto',
                            ...hideScrollbarStyle,
                            
                            backgroundColor: '#ffffff',
                            border: '1px solid #e0e0e0',
                            borderRadius: 2,
                            px: 2,
                            py: 1,
                        }}
                        >
                        {submissionResult.map((mapping, index) => (
                            <Box
                            key={index}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: 1,
                                borderBottom: index !== submissionResult.length - 1 ? '1px solid #e0e0e0' : 'none',
                                fontFamily: 'monospace',
                                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                            }}
                            >
                            <Typography variant="body2" noWrap>
                                Form ID: {mapping.form_number}
                            </Typography>
                            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                SQL ID: {mapping.sale_id}
                            </Typography>
                            </Box>
                        ))}
                        </Box>
                
                        <Button
                            variant="contained"
                    
                            onClick={() => setSubmissionResult(null)}
                            size="small"
                            
                            sx={{
                                mt: 2,
                                flex: 3,
                                height: '100%',
                                whiteSpace: 'nowrap',
                                width: { xs: '100%', sm: '30%' },
                                background: 'linear-gradient(to right, #1976d2, #0d47a1)',
                                color: '#fff',
                                fontWeight: 'bold',
                                '&:hover': {
                                background: 'linear-gradient(to right, #1565c0, #003c8f)',
                                },
                            }}
                            >
                        
                        CLEAR RESULTS
                        </Button>
                    </Paper>
                    )}
                    
                </Box> 
                </Container>
                )}

                <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircleHelp size={20} /> How to use Smart Daily
                    </DialogTitle>
                    <DialogContent dividers>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>
                            1. Start from a photo or from scratch
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            <strong>Select Image</strong> then <strong>Process File</strong> reads a photo of the
                            paper log and fills the table for you. Or use <strong>Add Sale</strong> to type a line
                            by hand.
                        </Typography>

                        <Divider sx={{ my: 1.5 }} />

                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            2. Write the description
                        </Typography>
                        <Typography variant="body2" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
                            Quantity, then the service abbreviation. Join services with <strong>+</strong>:
                            <Box component="code" sx={{ display: 'block', my: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                                1 CB + 2 CH
                            </Box>
                            As you type, a list of valid abbreviations appears — click one, or press
                            <strong> Tab</strong> / <strong>Enter</strong> to accept the first. Half quantities are
                            allowed where the service supports it (<code>0.5 WH</code>).
                        </Typography>

                        <Divider sx={{ my: 1.5 }} />

                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            3. Fix what turns red
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            <strong>Unknown Item</strong> means the abbreviation does not exist — use the
                            suggestions to correct it. <strong>Invalid quantity</strong> means the number is
                            missing or zero. The grey line under the field shows how the system understood you;
                            the total only updates once the line is valid.
                        </Typography>

                        <Divider sx={{ my: 1.5 }} />

                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            4. Payment, tip and submit
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Pick the payment method for each sale and fill the tip (leave empty if none).
                            Name, resort and date come from your schedule and cannot be edited here. Review the
                            totals and submit — sales are recorded under your name for today.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setHelpOpen(false)} variant="contained">Got it</Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    sx={{ bottom: { xs: 70, sm: 20 } }}
                >
                    <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }} elevation={6} variant="filled">
                        {snackbar.message}
                    </Alert>
                </Snackbar>

            </Box> 
        </Container> 
    );
};

export default EmployeeForm;